import { z } from 'zod';

/**
 * Messaging contracts (Module 6). Backs `ChatMessage`/`EmailNotification`
 * in schema.prisma. A message always belongs to a Booking — clients and
 * therapists only ever message each other in the context of a session
 * they've booked, never cold.
 */

export const ChatMessageSchema = z.object({
  id: z.string().cuid(),
  bookingId: z.string().cuid(),
  senderId: z.string().cuid(),
  recipientId: z.string().cuid(),
  content: z.string(),
  attachmentId: z.string().cuid().nullable(),
  attachmentUrl: z.string().url().nullable(),
  sentAt: z.string().datetime(),
  readAt: z.string().datetime().nullable(),
});
export type ChatMessageDto = z.infer<typeof ChatMessageSchema>;

/** POST /api/v1/chat/messages */
export const SendMessageSchema = z.object({
  bookingId: z.string().cuid(),
  content: z.string().trim().min(1).max(4000),
  attachmentId: z.string().cuid().optional(),
});
export type SendMessageInput = z.infer<typeof SendMessageSchema>;

/**
 * GET /api/v1/chat/messages/:bookingId?after=<cursor>&limit=<n>
 * `after` is the `id` of the last message the client already has — lets a
 * reconnecting client (offline mode) fetch only what it missed instead of
 * re-downloading the whole thread.
 */
export const ListMessagesQuerySchema = z.object({
  after: z.string().cuid().optional(),
  limit: z.coerce.number().int().min(1).max(100).default(50),
});
export type ListMessagesQuery = z.infer<typeof ListMessagesQuerySchema>;

export const ChatMessageListResponseSchema = z.object({
  messages: z.array(ChatMessageSchema),
  hasMore: z.boolean(),
});
export type ChatMessageListResponse = z.infer<typeof ChatMessageListResponseSchema>;

/** POST /api/v1/chat/attachments (multipart) response. */
export const ChatAttachmentSchema = z.object({
  mediaId: z.string().cuid(),
  url: z.string().url(),
  mimeType: z.string(),
  bytes: z.number().int().nonnegative(),
});
export type ChatAttachment = z.infer<typeof ChatAttachmentSchema>;

// ─── WebSocket event envelope (GET /api/v1/chat/ws) ────────────────────
// Client → server, first frame, required before anything else is accepted:
export const WsAuthFrameSchema = z.object({
  type: z.literal('auth'),
  token: z.string(),
  bookingId: z.string().cuid(),
});
export type WsAuthFrame = z.infer<typeof WsAuthFrameSchema>;

// Client → server, after auth:
export const WsTypingFrameSchema = z.object({
  type: z.literal('typing'),
  isTyping: z.boolean(),
});
export type WsTypingFrame = z.infer<typeof WsTypingFrameSchema>;

export const WsClientFrameSchema = z.discriminatedUnion('type', [WsAuthFrameSchema, WsTypingFrameSchema]);
export type WsClientFrame = z.infer<typeof WsClientFrameSchema>;

// Server → client:
export type WsServerFrame =
  | { type: 'auth_ok' }
  | { type: 'error'; message: string }
  | { type: 'message'; message: ChatMessageDto }
  | { type: 'read'; byUserId: string; readAt: string }
  | { type: 'typing'; userId: string; isTyping: boolean };
